import {
    Button,
    Divider,
    Flex,
    Group,
    IconFont,
    Input,
    Popover,
    SegmentedControl,
    singleTextEllipsis,
    Text,
    Toast,
    Tooltip
} from '@byecode/ui'
import type { DragEndEvent, DragStartEvent } from '@dnd-kit/core'
import { DndContext, MouseSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core'
import { restrictToFirstScrollableAncestor, restrictToVerticalAxis } from '@dnd-kit/modifiers'
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import type { TabBaseConfig, TabConfig, TabsType, VariableADTvalue } from '@lighthouse/core'
import { ALIGNMENT, BACKGROUND_TYPE } from '@lighthouse/core'
import {
    fieldConvertValue,
    FilledInput,
    FillPickerPopoverDropdown,
    getImageFullUrlInApplication,
    getTextColorStyle,
    IconPicker,
    IconRender,
    ListItem4ByecodeUi,
    useAtomData
} from '@lighthouse/shared'
import { nanoid } from '@lighthouse/tools'
import produce from 'immer'
import type { CSSProperties } from 'react'
import React, { forwardRef, useCallback, useDeferredValue, useMemo, useRef, useState } from 'react'
import { Controller, useFieldArray, useFormContext } from 'react-hook-form'
import styled from 'styled-components'

import { applicationSettingAtom } from '@/atoms/application/state'
import { selectedBlockCustomViewAtom } from '@/atoms/page/state'
import { EventList } from '@/components/EventList'
import { TAB_TYPE_COLOR_MAP } from '@/constants/Block/generate/tabs'
import { useCurrentAppId } from '@/hooks/useApplication'
import { useColorSystemAction } from '@/hooks/useColorSystemAction'
import { useGetColorVariableOptions } from '@/hooks/useGetColorVariableOptions'
import { useIsDisabledWithVersion } from '@/hooks/useIsDisabledWithVersion'
import { useParseVariableWithSubscription } from '@/hooks/useParseVariable'
import { useVariableCustomViewOption } from '@/hooks/useVariableCustomViewOption'
import { uploadManagerInAppParams } from '@/utils/auth'

import { ConditionFilter } from '../../ConditionFilter'
import { applicationAtom } from '../atoms'

type TabsFormSlice = {
    [key: string]: any
    baseList?: TabConfig[]
    variantMap?: Record<string, { align?: string; styleList: Array<Record<string, any>> }>
}

const TabBarSettingsWrapper = styled.div`
    padding: 0 8px;
`

const TabGroupItem = styled(Group)`
    padding: 0;
    border-radius: 8px;
    border: 1px solid var(--color-gray-200);
    overflow: hidden;
    & + & {
        margin-top: 12px;
    }
    & > .byecode-group-wrapper {
        padding: 0 8px;
        background-color: var(--color-gray-100);
    }
    & > .byecode-group-collapse {
        background-color: #fff;
    }
`
const TabGroupItemLabel = styled.span`
    font-weight: normal;
    ${singleTextEllipsis()}
`

const CollapseButton = styled.button`
    all: unset;
    box-sizing: border-box;
    cursor: pointer;
    appearance: none;
    display: flex;
    align-items: center;
    background-color: rgba(38, 65, 90, 0.06);
    padding: 6px;
    border-radius: 50%;
    &:hover {
        background-color: var(--color-gray-200);
    }
`

interface TabBarSettingsProps {
    isSegmentedType: boolean
    variant: TabsType
}

/** 标签栏标签设置 */
const TabBarSettings = forwardRef<HTMLDivElement, TabBarSettingsProps>(({ variant, isSegmentedType }, ref) => {
    const { control, setValue, getValues } = useFormContext<TabsFormSlice>()

    const disabledWithVersion = useIsDisabledWithVersion()
    const { fields, append, move, remove } = useFieldArray<TabsFormSlice, 'baseList', 'key'>({
        control,
        name: 'baseList',
        keyName: 'key'
    })

    const [activeId, setActiveId] = useState<string>()

    const countRef = useRef(fields.length)

    const sensors = useSensors(
        useSensor(MouseSensor, {
            activationConstraint: { distance: 8 }
        }),
        useSensor(TouchSensor, {
            activationConstraint: { distance: 8 }
        })
    )
    const onDragStart = (e: DragStartEvent) => {
        if (e.active) {
            setActiveId(e.active.id as string)

            if (openedIndex !== -1) {
                setOpenedIndex(-1)
            }
        }
    }

    const onDragEnd = ({ over }: DragEndEvent) => {
        if (over && activeId) {
            const overIndex = fields.findIndex(item => item.id === over.id)
            const activeIndex = fields.findIndex(item => item.id === activeId)
            if (activeIndex !== overIndex) {
                move(activeIndex, overIndex)
            }
        }
        setActiveId(undefined)
    }

    const onCreate = () => {
        const id = nanoid()
        const key = (++countRef.current).toString().padStart(2, '0')
        append({
            id,
            name: `标签 ${key}`,
            tabIcon: { type: 'icon', value: '' },
            iconPosition: ALIGNMENT.left,
            eventList: [] /* associatedView: '' */ /* sizeFit: SIZE_FIT.contain */
        })

        // 同步添加style list配置
        const variantMap = getValues(`variantMap`)
        const newVarianMap = produce(variantMap ?? {}, draft => {
            Object.entries(draft).forEach(([k, v]) => {
                v.styleList.push({ id, ...TAB_TYPE_COLOR_MAP[k as TabsType] })
            })
        })
        setValue(`variantMap`, newVarianMap)
    }

    const onDelete = (index: number) => {
        if (fields.length === 1) {
            Toast.warning('不可删除，至少需要1个tab')
            return
        }
        remove(index)

        // 同步删除style list配置
        const { id } = fields[index]
        const variantMap = getValues(`variantMap`)
        const newVarianMap = produce(variantMap ?? {}, draft => {
            Object.values(draft).forEach(v => {
                const fromStyleIndex = v.styleList.findIndex(item => item.id === id)
                if (fromStyleIndex === -1) {
                    return
                }
                v.styleList.splice(fromStyleIndex, 1)
            })
        })

        setValue(`variantMap`, newVarianMap)
    }

    const onCopy = (index: number) => {
        const id = nanoid()

        const fromData = fields[index]

        // 同步复制style list的配置
        const variantMap = getValues(`variantMap`)
        const newVarianMap = produce(variantMap ?? {}, draft => {
            Object.values(draft).forEach(v => {
                const fromStyle = v.styleList.find(item => item.id === fromData.id)
                v.styleList.push({ ...fromStyle, id })
            })
        })

        append({ ...fromData, id, name: `${fromData.name} 副本` })
        setValue(`variantMap`, newVarianMap)
        setOpenedIndex(index + 1)
    }

    const [openedIndex, setOpenedIndex] = useState(-1)

    const items = useMemo(() => (isSegmentedType ? fields.slice(0, 5) : fields), [isSegmentedType, fields])

    return (
        <TabBarSettingsWrapper ref={ref}>
            <div style={{ overflow: 'auto' }}>
                <DndContext
                    sensors={sensors}
                    modifiers={[restrictToVerticalAxis, restrictToFirstScrollableAncestor]}
                    onDragStart={onDragStart}
                    onDragEnd={onDragEnd}
                    onDragCancel={() => setActiveId(undefined)}
                >
                    <SortableContext items={items.map(item => item.id)} strategy={verticalListSortingStrategy}>
                        {items.map((item, index) => (
                            <SortableItem
                                disabled={disabledWithVersion}
                                key={item.id}
                                index={index}
                                data={item}
                                onDelete={onDelete}
                                onCopy={onCopy}
                                variant={variant}
                                opened={openedIndex === index}
                                onOpenedChange={v => {
                                    setOpenedIndex(v ? index : -1)
                                }}
                            />
                        ))}
                    </SortableContext>
                </DndContext>
            </div>
            <Flex alignItems="center" gap="10px" style={{ margin: '12px 0' }}>
                <Button
                    disabled={(isSegmentedType && items.length === 5) || disabledWithVersion}
                    radius={100}
                    icon={<IconFont type="Add" />}
                    onClick={onCreate}
                >
                    添加
                </Button>
                {isSegmentedType && (
                    <Tooltip title="胶囊类型标签最多显示5个选项">
                        <IconFont type="Question" size={16} fill="var(--color-gray-400)" />
                    </Tooltip>
                )}
            </Flex>
        </TabBarSettingsWrapper>
    )
})

/** ************************************************ */
/** **************** Dnd Sort Item ***************** */
/** ************************************************ */
const ICON_POSITION_OPTIONS = [
    {
        label: '左',
        value: ALIGNMENT.left
    },
    {
        label: '右',
        value: ALIGNMENT.right
    }
]

interface SortableItemProps {
    data: TabConfig
    index: number
    disabled?: boolean
    onDelete: (i: number) => void
    onCopy: (i: number) => void
    variant: TabsType

    opened: boolean
    onOpenedChange: (v: boolean) => void
}

const SortableItem: React.FC<SortableItemProps> = ({ variant, data, index, disabled, onDelete, onCopy, opened, onOpenedChange }) => {
    const appId = useCurrentAppId()
    const { setNodeRef, setActivatorNodeRef, listeners, attributes, transform, transition } = useSortable({
        id: data.id,
        attributes: { role: 'li' },
        disabled
    })
    const customViewDsId = useDeferredValue(
        useAtomData(
            selectedBlockCustomViewAtom,
            useCallback(s => s?.config.pointer, [])
        )
    )
    const { control, watch } = useFormContext<TabsFormSlice>()

    const [name, tabStyle] = watch([`baseList.${index}.name`, `variantMap.${variant}.styleList.${index}`])

    const allRichtext = useMemo(
        () =>
            [
                tabStyle.background?.image?.resource,
                tabStyle.active?.background?.image?.resource,
                tabStyle.hover?.background?.image?.resource
            ].filter(Boolean),
        [tabStyle.active?.background?.image?.resource, tabStyle.background?.image?.resource, tabStyle.hover?.background?.image?.resource]
    )
    const parseVariable = useParseVariableWithSubscription(allRichtext)
    const parseVariable2String = useCallback(
        (value: VariableADTvalue) => {
            const field = parseVariable(value)
            if (!field) {
                return ''
            }

            return fieldConvertValue(field, { useFileUrl: true })
        },
        [parseVariable]
    )

    const style: CSSProperties = {
        transform: CSS.Translate.toString(transform),
        transition
    }

    const palettes = useAtomData(
        applicationSettingAtom,
        useCallback(s => {
            return s?.theme.palettes ?? []
        }, [])
    )
    const colorSystemMethods = useColorSystemAction()

    const variableTypeOption = useGetColorVariableOptions()

    return (
        <TabGroupItem
            ref={setNodeRef}
            style={style}
            opened={opened}
            onCollapseChange={onOpenedChange}
            label={
                <Flex gap="6px" alignItems="center">
                    {!disabled && (
                        <IconFont type="DotsSix" color="var(--color-gray-400)" ref={setActivatorNodeRef} {...listeners} {...attributes} />
                    )}
                    {data.tabIcon?.value && (
                        <IconRender
                            size={16}
                            isCompact
                            readonly
                            value={data.tabIcon}
                            color={getTextColorStyle(data.iconColor, palettes).color}
                            onGetFullUrl={url => getImageFullUrlInApplication(appId, url)}
                        />
                    )}
                    {/* {data.tabIcon && <IconFont type={data.icon} style={{ ...getTextColorStyle(data.iconColor, palettes) }} />} */}
                    <TabGroupItemLabel>{data.name}</TabGroupItemLabel>
                </Flex>
            }
            extra={
                <Flex gap={6} alignItems="center">
                    <Button
                        type="text"
                        icon={<IconFont type="Duplicate" size={16} fill="var(--color-gray-400)" />}
                        onClick={e => {
                            e.stopPropagation()
                            onCopy(index)
                        }}
                        radius={5}
                        style={{ padding: 2, width: 20, height: 20 }}
                    />
                    <Button
                        type="text"
                        icon={<IconFont type="Trash" size={16} fill="var(--color-gray-400)" />}
                        onClick={() => {
                            onDelete(index)
                        }}
                        radius={5}
                        style={{ padding: 2, width: 20, height: 20 }}
                    />
                </Flex>
            }
        >
            <Group collapsible={false} label="基础" mode="none" mx={-8}>
                {/* <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                    <Text>绑定至</Text>
                    <Controller
                        control={control}
                        name={`baseList.${index}.associatedView`}
                        render={({ field }) => <Select options={viewListOptions} {...field} styles={{ root: { width: 180 } }} />}
                    />
                </ListItem4ByecodeUi> */}

                <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                    <Text>标题</Text>
                    <Controller
                        control={control}
                        name={`baseList.${index}.name`}
                        render={({ field }) => (
                            <Input
                                style={{ width: 180 }}
                                // maxLength={20}
                                defaultValue={field.value}
                                onKeyDown={e => {
                                    if (e.code === 'Enter') {
                                        field.onChange(e.currentTarget.value)
                                        e.currentTarget.blur()
                                    }
                                }}
                                onBlur={e => field.onChange(e.target.value)}
                            />
                        )}
                    />
                </ListItem4ByecodeUi>
                <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                    <Text>图标</Text>
                    <Controller
                        control={control}
                        name={`baseList.${index}.tabIcon`}
                        render={({ field }) => (
                            <Tooltip disabled={disabled} title={field.value?.value ? '更换图标' : '添加图标'}>
                                <IconPicker
                                    {...field}
                                    clearable
                                    uploadParams={uploadManagerInAppParams()}
                                    onGetFullUrl={url => getImageFullUrlInApplication(appId, url)}
                                    allowIconTypes={['face', 'line', 'upload']}
                                />
                            </Tooltip>
                        )}
                    />
                </ListItem4ByecodeUi>
                <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                    <Text>图标位置</Text>
                    <Controller
                        control={control}
                        name={`baseList.${index}.iconPosition`}
                        render={({ field }) => (
                            <SegmentedControl data={ICON_POSITION_OPTIONS} fullWidth style={{ width: 180 }} {...field} />
                        )}
                    />
                </ListItem4ByecodeUi>
                {/* <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                    <Text>宽度规则</Text>
                    <Controller
                        control={control}
                        name={`baseList.${index}.sizeFit`}
                        render={({ field }) => (
                            <SegmentedControl {...field} data={SIZE_FIT_TYPE_CONTROL} fullWidth style={{ width: 180 }} />
                        )}
                    />
                </ListItem4ByecodeUi> */}
                <Controller
                    control={control}
                    name={`baseList.${index}.visibilityFilter`}
                    render={({ field }) => (
                        <ConditionFilter
                            label="显示条件"
                            viewDsId={customViewDsId}
                            viewType="custom"
                            filter={field.value}
                            onFilter={field.onChange}
                            enableHover={false}
                            style={{ cursor: 'pointer' }}
                        />
                    )}
                />
            </Group>

            <Divider />

            <Controller
                name={`baseList.${index}.eventList`}
                control={control}
                render={({ field }) => (
                    <EventList style={{ margin: '0 -8px' }} blockType="tabs" value={field.value || []} onChange={field.onChange} />
                )}
            />

            <Divider />

            <Group collapsible={false} label="颜色" mode="none" mx={-8}>
                <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                    <Text>图标</Text>
                    <Controller
                        control={control}
                        name={`variantMap.${variant}.styleList.${index}.iconColor`}
                        render={({ field }) => (
                            <Popover width={300} withinPortal position="left-start" autoUpdate={false} offsetOptions={152}>
                                <Popover.Target>
                                    <FilledInput value={field.value} onChange={field.onChange} style={{ width: 180 }} />
                                </Popover.Target>
                                <FillPickerPopoverDropdown
                                    title="填充"
                                    value={field.value}
                                    onChange={field.onChange}
                                    {...colorSystemMethods}
                                />
                                {/* <PopoverDropdownWithClose title="填充">
                                    <FillPicker value={field.value} onChange={field.onChange} />
                                </PopoverDropdownWithClose> */}
                            </Popover>
                        )}
                    />
                </ListItem4ByecodeUi>

                <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                    <Text>文本</Text>
                    <Controller
                        control={control}
                        name={`variantMap.${variant}.styleList.${index}.color`}
                        render={({ field }) => (
                            <Popover width={300} withinPortal position="left-start" autoUpdate={false} offsetOptions={152}>
                                <Popover.Target>
                                    <FilledInput value={field.value} onChange={field.onChange} style={{ width: 180 }} />
                                </Popover.Target>
                                {/* <PopoverDropdownWithClose title="填充">
                                    <FillPicker
                                        value={field.value}
                                        onChange={field.onChange}
                                        enabledBackgroundTypes={[BACKGROUND_TYPE.color, BACKGROUND_TYPE.gradient]}
                                    />
                                </PopoverDropdownWithClose> */}
                                <FillPickerPopoverDropdown
                                    title="填充"
                                    value={field.value}
                                    onChange={field.onChange}
                                    enabledBackgroundTypes={[BACKGROUND_TYPE.color, BACKGROUND_TYPE.gradient]}
                                    {...colorSystemMethods}
                                />
                            </Popover>
                        )}
                    />
                </ListItem4ByecodeUi>

                <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                    <Text>背景</Text>
                    <Controller
                        control={control}
                        name={`variantMap.${variant}.styleList.${index}.background`}
                        render={({ field }) => (
                            <Popover width={300} withinPortal position="left-start" autoUpdate={false} offsetOptions={152}>
                                <Popover.Target>
                                    <FilledInput
                                        value={field.value}
                                        onChange={field.onChange}
                                        getImageVariableUrl={parseVariable2String}
                                        variableTypeOption={variableTypeOption}
                                        style={{ width: 180 }}
                                    />
                                </Popover.Target>
                                <FillPickerPopoverDropdown
                                    title="填充"
                                    value={field.value}
                                    onChange={field.onChange}
                                    variableTypeOption={variableTypeOption}
                                    enabledBackgroundTypes={[
                                        BACKGROUND_TYPE.color,
                                        BACKGROUND_TYPE.gradient,
                                        BACKGROUND_TYPE.image,
                                        BACKGROUND_TYPE.variable
                                    ]}
                                    {...colorSystemMethods}
                                />
                            </Popover>
                        )}
                    />
                </ListItem4ByecodeUi>

                <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                    <Text>描边</Text>
                    <Controller
                        control={control}
                        name={`variantMap.${variant}.styleList.${index}.borderColor`}
                        render={({ field }) => (
                            <Popover width={300} withinPortal position="left-start" autoUpdate={false} offsetOptions={152}>
                                <Popover.Target>
                                    <FilledInput value={field.value} onChange={field.onChange} style={{ width: 180 }} />
                                </Popover.Target>
                                {/* <PopoverDropdownWithClose title="填充">
                                    <FillPicker value={field.value} onChange={field.onChange} />
                                </PopoverDropdownWithClose> */}
                                <FillPickerPopoverDropdown
                                    title="填充"
                                    value={field.value}
                                    onChange={field.onChange}
                                    {...colorSystemMethods}
                                />
                            </Popover>
                        )}
                    />
                </ListItem4ByecodeUi>

                <Group
                    label="悬浮态"
                    defaultOpen={false}
                    renderRightIcon={(open, onChange) => {
                        return (
                            <CollapseButton onClick={() => onChange(!open)}>
                                <IconFont
                                    type="ArrowDownSmall"
                                    size={16}
                                    color="var(--color-gray-400)"
                                    style={{ transform: open ? 'rotate(-180deg)' : undefined, transition: 'transform 0.2s' }}
                                />
                            </CollapseButton>
                        )
                    }}
                    styles={{
                        root: { margin: '0 -8px', padding: 0 },
                        label: { marginLeft: 'auto', fontWeight: 400 },
                        rightIcon: { marginLeft: 'unset' }
                    }}
                >
                    <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                        <Text>图标</Text>
                        <Controller
                            control={control}
                            name={`variantMap.${variant}.styleList.${index}.hover.iconColor`}
                            render={({ field }) => (
                                <Popover width={300} withinPortal position="left-start" autoUpdate={false} offsetOptions={152}>
                                    <Popover.Target>
                                        <FilledInput value={field.value} onChange={field.onChange} style={{ width: 180 }} />
                                    </Popover.Target>
                                    {/* <PopoverDropdownWithClose title="填充">
                                        <FillPicker value={field.value} onChange={field.onChange} />
                                    </PopoverDropdownWithClose> */}
                                    <FillPickerPopoverDropdown
                                        title="填充"
                                        value={field.value}
                                        onChange={field.onChange}
                                        {...colorSystemMethods}
                                    />
                                </Popover>
                            )}
                        />
                    </ListItem4ByecodeUi>

                    <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                        <Text>文本</Text>
                        <Controller
                            control={control}
                            name={`variantMap.${variant}.styleList.${index}.hover.color`}
                            render={({ field }) => (
                                <Popover width={300} withinPortal position="left-start" autoUpdate={false} offsetOptions={152}>
                                    <Popover.Target>
                                        <FilledInput value={field.value} onChange={field.onChange} style={{ width: 180 }} />
                                    </Popover.Target>
                                    {/* <PopoverDropdownWithClose title="填充">
                                        <FillPicker
                                            value={field.value}
                                            onChange={field.onChange}
                                            enabledBackgroundTypes={[BACKGROUND_TYPE.color, BACKGROUND_TYPE.gradient]}
                                        />
                                    </PopoverDropdownWithClose> */}
                                    <FillPickerPopoverDropdown
                                        title="填充"
                                        value={field.value}
                                        onChange={field.onChange}
                                        enabledBackgroundTypes={[BACKGROUND_TYPE.color, BACKGROUND_TYPE.gradient]}
                                        {...colorSystemMethods}
                                    />
                                </Popover>
                            )}
                        />
                    </ListItem4ByecodeUi>

                    <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                        <Text>背景</Text>
                        <Controller
                            control={control}
                            name={`variantMap.${variant}.styleList.${index}.hover.background`}
                            render={({ field }) => (
                                <Popover width={300} withinPortal position="left-start" autoUpdate={false} offsetOptions={152}>
                                    <Popover.Target>
                                        <FilledInput
                                            value={field.value}
                                            onChange={field.onChange}
                                            getImageVariableUrl={parseVariable2String}
                                            variableTypeOption={variableTypeOption}
                                            style={{ width: 180 }}
                                        />
                                    </Popover.Target>
                                    <FillPickerPopoverDropdown
                                        title="填充"
                                        value={field.value}
                                        onChange={field.onChange}
                                        enabledBackgroundTypes={[
                                            BACKGROUND_TYPE.color,
                                            BACKGROUND_TYPE.gradient,
                                            BACKGROUND_TYPE.image,
                                            BACKGROUND_TYPE.variable
                                        ]}
                                        variableTypeOption={variableTypeOption}
                                        {...colorSystemMethods}
                                    />
                                </Popover>
                            )}
                        />
                    </ListItem4ByecodeUi>

                    <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                        <Text>描边</Text>
                        <Controller
                            control={control}
                            name={`variantMap.${variant}.styleList.${index}.hover.borderColor`}
                            render={({ field }) => (
                                <Popover width={300} withinPortal position="left-start" autoUpdate={false} offsetOptions={152}>
                                    <Popover.Target>
                                        <FilledInput value={field.value} onChange={field.onChange} style={{ width: 180 }} />
                                    </Popover.Target>
                                    {/* <PopoverDropdownWithClose title="填充">
                                        <FillPicker value={field.value} onChange={field.onChange} />
                                    </PopoverDropdownWithClose> */}
                                    <FillPickerPopoverDropdown
                                        title="填充"
                                        value={field.value}
                                        onChange={field.onChange}
                                        {...colorSystemMethods}
                                    />
                                </Popover>
                            )}
                        />
                    </ListItem4ByecodeUi>
                </Group>

                <Group
                    label="选中态"
                    defaultOpen={false}
                    renderRightIcon={(open, onChange) => {
                        return (
                            <CollapseButton onClick={() => onChange(!open)}>
                                <IconFont
                                    type="ArrowDownSmall"
                                    size={16}
                                    color="var(--color-gray-400)"
                                    style={{ transform: open ? 'rotate(-180deg)' : undefined, transition: 'transform 0.2s' }}
                                />
                            </CollapseButton>
                        )
                    }}
                    styles={{
                        root: { margin: '0 -8px', padding: 0 },
                        label: { marginLeft: 'auto', fontWeight: 400 },
                        rightIcon: { marginLeft: 'unset' }
                    }}
                >
                    <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                        <Text>图标</Text>
                        <Controller
                            control={control}
                            name={`variantMap.${variant}.styleList.${index}.active.iconColor`}
                            render={({ field }) => (
                                <Popover width={300} withinPortal position="left-start" autoUpdate={false} offsetOptions={152}>
                                    <Popover.Target>
                                        <FilledInput value={field.value} onChange={field.onChange} style={{ width: 180 }} />
                                    </Popover.Target>
                                    {/* <PopoverDropdownWithClose title="填充">
                                        <FillPicker value={field.value} onChange={field.onChange} />
                                    </PopoverDropdownWithClose> */}
                                    <FillPickerPopoverDropdown
                                        title="填充"
                                        value={field.value}
                                        onChange={field.onChange}
                                        {...colorSystemMethods}
                                    />
                                </Popover>
                            )}
                        />
                    </ListItem4ByecodeUi>

                    <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                        <Text>文本</Text>
                        <Controller
                            control={control}
                            name={`variantMap.${variant}.styleList.${index}.active.color`}
                            render={({ field }) => (
                                <Popover width={300} withinPortal position="left-start" autoUpdate={false} offsetOptions={152}>
                                    <Popover.Target>
                                        <FilledInput value={field.value} onChange={field.onChange} style={{ width: 180 }} />
                                    </Popover.Target>
                                    {/* <PopoverDropdownWithClose title="填充">
                                        <FillPicker
                                            value={field.value}
                                            onChange={field.onChange}
                                            enabledBackgroundTypes={[BACKGROUND_TYPE.color, BACKGROUND_TYPE.gradient]}
                                        />
                                    </PopoverDropdownWithClose> */}
                                    <FillPickerPopoverDropdown
                                        title="填充"
                                        value={field.value}
                                        onChange={field.onChange}
                                        enabledBackgroundTypes={[BACKGROUND_TYPE.color, BACKGROUND_TYPE.gradient]}
                                        {...colorSystemMethods}
                                    />
                                </Popover>
                            )}
                        />
                    </ListItem4ByecodeUi>

                    <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                        <Text>背景</Text>
                        <Controller
                            control={control}
                            name={`variantMap.${variant}.styleList.${index}.active.background`}
                            render={({ field }) => (
                                <Popover width={300} withinPortal position="left-start" autoUpdate={false} offsetOptions={152}>
                                    <Popover.Target>
                                        <FilledInput
                                            value={field.value}
                                            onChange={field.onChange}
                                            getImageVariableUrl={parseVariable2String}
                                            variableTypeOption={variableTypeOption}
                                            style={{ width: 180 }}
                                        />
                                    </Popover.Target>
                                    <FillPickerPopoverDropdown
                                        title="填充"
                                        value={field.value}
                                        onChange={field.onChange}
                                        enabledBackgroundTypes={[
                                            BACKGROUND_TYPE.color,
                                            BACKGROUND_TYPE.gradient,
                                            BACKGROUND_TYPE.image,
                                            BACKGROUND_TYPE.variable
                                        ]}
                                        variableTypeOption={variableTypeOption}
                                        {...colorSystemMethods}
                                    />
                                </Popover>
                            )}
                        />
                    </ListItem4ByecodeUi>

                    <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                        <Text>描边</Text>
                        <Controller
                            control={control}
                            name={`variantMap.${variant}.styleList.${index}.active.borderColor`}
                            render={({ field }) => (
                                <Popover width={300} withinPortal position="left-start" autoUpdate={false} offsetOptions={152}>
                                    <Popover.Target>
                                        <FilledInput value={field.value} onChange={field.onChange} style={{ width: 180 }} />
                                    </Popover.Target>
                                    {/* <PopoverDropdownWithClose title="填充">
                                        <FillPicker value={field.value} onChange={field.onChange} />
                                    </PopoverDropdownWithClose> */}
                                    <FillPickerPopoverDropdown
                                        title="填充"
                                        value={field.value}
                                        onChange={field.onChange}
                                        {...colorSystemMethods}
                                    />
                                </Popover>
                            )}
                        />
                    </ListItem4ByecodeUi>
                </Group>
            </Group>
        </TabGroupItem>
    )
}

export default TabBarSettings
